import { observer } from 'mobx-react-lite';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { workAreaScope as appWorkAreaScope } from '@/stores';
import type { WorkAreaScope } from '@/stores/workAreaScope';

/**
 * I-0958 (WS-2, `docs/concept-work-area-scope.md`): the page renders only once every per-work-area
 * store is on the work area the page is about.
 *
 * While the scope is on another work area, this renders `fallback` and enters the new one in a
 * layout effect: before the browser paints, so the reader never sees the step, and before the page's
 * own effects, so no page renders a row of the previous work area. `workAreaId` of `null` or
 * `undefined` — a page about no work area, or an address still resolving — renders the page as it is.
 */
export const WorkAreaScopeGate = observer(function WorkAreaScopeGate({
  workAreaId,
  fallback,
  children,
  scope = appWorkAreaScope,
}: {
  workAreaId: string | null | undefined;
  fallback: ReactNode;
  children: ReactNode;
  scope?: WorkAreaScope;
}) {
  useLayoutEffect(() => {
    if (workAreaId) scope.enter(workAreaId);
  }, [scope, workAreaId]);
  const entered = !workAreaId || scope.current === workAreaId;
  /*
    I-1169 (the toolbar draft that vanished): a page already drawn under no work area keeps its children
    when the first work area appears over an EMPTY scope. `/-/settings` is an app address, so its page
    is "about" the selection, and a fresh person's selection arrives a second after the page does (the
    work-area list answers). Swapping to `fallback` there unmounted the whole page and mounted it again:
    Settings → Mobile toolbar lost the edit the reader had just made, and its checkbox sat disabled over a
    draft that was back at four entries. Nothing stale can show — there was no previous work area — and
    the layout effect above enters the new one before paint anyway. A scope on ANOTHER work area still
    swaps, which is what this gate is for.
  */
  const drawn = useRef(false);
  const keepPage = !entered && drawn.current && scope.current === null;
  const showChildren = entered || keepPage;
  useLayoutEffect(() => {
    drawn.current = showChildren;
  });
  return <>{showChildren ? children : fallback}</>;
});
